import React from 'react';
import styled, { css } from 'styled-components';

import { GrowthPlannerMetricOption } from '../../../types';
import { responseType, rsColor, rsHandoffColor, rsv } from '../../responseScale';
import { StepHeading } from './handoffParts';

// 6473:12805-12836: two columns of 472 under the heading; a taller card does not stretch its row.
const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  align-items: start;
  column-gap: ${rsv.space('metricColumnGap')}px;
  row-gap: ${rsv.space('metricRowGap')}px;
  margin-top: ${rsv.space('metricGridTop')}px;
`;

// 6473:12806: the box 17 at +19 / +17, filled #1C77F6 checked or not; the white tick marks a pick.
const Box = styled.svg`
  position: absolute;
  top: ${rsv.space('metricBoxTop')}px;
  left: ${rsv.space('metricBoxLeft')}px;
  width: ${rsv.size('checkbox')}px;
  height: ${rsv.size('checkbox')}px;
  border-radius: 4px;
`;

const Input = styled.input`
  position: absolute;
  top: ${rsv.space('metricBoxTop')}px;
  left: ${rsv.space('metricBoxLeft')}px;
  width: 1px;
  height: 1px;
  margin: 0;
  opacity: 0;

  &:focus-visible + ${Box} {
    outline: 2px solid ${rsColor.link};
    outline-offset: 2px;
  }
`;

// 6473:12805 / 12813: r12, 62 tall, the text at +51 / +15. Selected: #6558F5 at 7% under #3D8BFF.
// 6473:12821: an unavailable card at 0.55.
const Card = styled.label<{ $selected: boolean; $available: boolean; $inert: boolean }>`
  position: relative;
  display: block;
  box-sizing: border-box;
  min-height: ${rsv.size('metricCardMinHeight')}px;
  padding: ${rsv.space('metricCard')};
  border: 1px solid
    ${({ $selected }) => ($selected ? rsHandoffColor.selectedRule : rsHandoffColor.cardRule)};
  border-radius: ${rsv.radius('row')}px;
  background: ${({ $selected }) => ($selected ? rsHandoffColor.selectedFill : 'transparent')};
  cursor: ${({ $inert }) => ($inert ? 'default' : 'pointer')};

  ${({ $available }) =>
    $available
      ? null
      : css`
          opacity: 0.55;
        `}
`;

const Title = styled.span`
  display: block;
  color: ${rsColor.text};
  ${responseType('metricTitle')}
`;

const Meta = styled.span`
  display: block;
  margin-top: ${rsv.space('metricMetaGap')}px;
  color: ${rsColor.secondary};
  ${responseType('metricMeta')}
`;

// 6473:12828.
const Reason = styled.span`
  display: block;
  margin-top: ${rsv.space('metricReasonGap')}px;
  color: ${rsHandoffColor.unavailable};
  ${responseType('metricMeta')}
`;

const MetricBox: React.FC<{ checked: boolean }> = ({ checked }) => (
  <Box width={17} height={17} viewBox="0 0 17 17" fill="none" aria-hidden="true" focusable="false">
    <rect
      x={1.4}
      y={1.4}
      width={14.2}
      height={14.2}
      rx={3}
      fill={rsHandoffColor.checkbox}
      stroke={rsHandoffColor.checkbox}
      strokeWidth={2}
    />
    {checked ? (
      <path
        d="M4.2 8.4 7.1 11.4 12.7 5.7"
        stroke={rsColor.white}
        strokeWidth={2.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    ) : null}
  </Box>
);

export interface IHandoffMetricsProps {
  heading: string;
  options: GrowthPlannerMetricOption[];
  /** The picked option ids. */
  selected: string[];
  /** Once the hand-off has started, nothing can be picked. */
  locked: boolean;
  onToggle: (id: string) => void;
}

/** Figma 6473:12804-12836: "1 · Choose actionable metrics" and its option cards. */
const HandoffMetrics: React.FC<IHandoffMetricsProps> = ({
  heading,
  options,
  selected,
  locked,
  onToggle
}) => (
  <section data-testid="orbit-handoff-metrics">
    <StepHeading>{heading}</StepHeading>
    <Grid>
      {options.map(option => {
        const checked = selected.includes(option.id);
        const inert = locked || !option.available;
        return (
          <Card
            key={option.id}
            $selected={checked}
            $available={option.available}
            $inert={inert}
            data-testid="orbit-handoff-metric"
            data-metric-id={option.id}
          >
            <Input
              type="checkbox"
              checked={checked}
              disabled={inert}
              onChange={() => onToggle(option.id)}
            />
            <MetricBox checked={checked} />
            <Title>{option.title}</Title>
            <Meta>{option.meta.join(' | ')}</Meta>
            {!option.available && option.unavailable_reason ? (
              <Reason>{option.unavailable_reason}</Reason>
            ) : null}
          </Card>
        );
      })}
    </Grid>
  </section>
);

export default HandoffMetrics;
